import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "rhombus-react";
import { useFlag } from '@innovid/feature-flags-react';
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';
import {
	CREATE_MOCKUP,
	createMockup,
	previewCreatives,
	previewCreativesLegacy,
	previewBulkCreativeVersions,
	previewBulkCreativeVersionsLegacy,
	PREVIEW_CREATIVE,
	SHARE_CREATIVE,
	PREVIEW_VERSIONS,
	SHARE_VERSIONS
} from '../grid/cells/CreativeActionLists/PreviewActions';
import { openModal } from "reducers/Modal";
import { selectedCreativeData } from "selectors/Selected";
import { MAX_SHARED_CREATIVES, MODAL_TYPES } from "utils/constants";
import { isSocialPreviewDisabled } from 'utils/common';
import{ isInvalidDVASTState, shouldDisplayAction } from 'utils/CreativeActions';
import { PreviewCreative } from "utils/Preview";

import './BulkPreviewButton.css';

export function BulkPreviewButton(){
	const dispatch = useDispatch();
	let {
		selectedItems,
		libraryId,
		user,
	} = useSelector(state=> ({
		selectedItems: selectedCreativeData(state),
		libraryId: state.libraryProperties.id,
		user: state.you,
	}));
	const forceNewPreview = useFlag(FEATURE_FLAGS.NEW_PREVIEW_LINKS);

	let legacyPreviewCreativeIds = []
		, legacyPreviewVersionIds = []
		, previewCreativeIds = []
		, shareCreativeIds = []
		, previewVersionsIds = []
		, shareVersionsIds = []
		, splitActions = []
		, divider = false;

	const pluralize = (ids) => {
		return `${ids.length > 1 ? 's' : ''}`;
	};
	const isItemsInvalidDVAST = selectedItems.every(isInvalidDVASTState);

	if(selectedItems && selectedItems.length){
		selectedItems.forEach((ea)=>{
			const isLegacyPreview = PreviewCreative.private(ea, forceNewPreview).isLegacy;
			if(shouldDisplayAction.creativePreview(ea) && !isSocialPreviewDisabled(ea)){
				if (isLegacyPreview) {
					legacyPreviewCreativeIds.push(ea.creativeId);
				} else {
					previewCreativeIds.push(ea.creativeId);
				}
			}
			if(shouldDisplayAction.previewVersions(ea) && !isSocialPreviewDisabled(ea)){
				if (isLegacyPreview) {
					legacyPreviewVersionIds.push(ea.creativeId);
				} else {
					previewVersionsIds.push(ea.creativeId);
				}
			}
			if(SHARE_CREATIVE.shouldDisplay({ dependentValues: ea, user })){
				shareCreativeIds.push(ea.creativeId);
			}
			if(SHARE_VERSIONS.shouldDisplay({ dependentValues: ea, user })){
				shareVersionsIds.push(ea.creativeId);
			}
		});
	}

	let previewActions = {
		previewCreativePrivate:{
			icon:"fas fa-image",
			idArray:legacyPreviewCreativeIds,
			onClick:()=> previewCreativesLegacy(legacyPreviewCreativeIds),
			name: `Preview Creative${pluralize(legacyPreviewCreativeIds)} (Private) (${legacyPreviewCreativeIds.length}/${MAX_SHARED_CREATIVES})`,
			disabled: legacyPreviewCreativeIds.length > MAX_SHARED_CREATIVES,
		},
		previewCreative:{
			...PREVIEW_CREATIVE,
			idArray: previewCreativeIds,
			onClick:()=> previewCreatives(previewCreativeIds),
			name: `Preview Creative${pluralize(previewCreativeIds)} (${previewCreativeIds.length}/${MAX_SHARED_CREATIVES})`,
			className: "bulk-external-link-action",
			disabled: previewCreativeIds.length > MAX_SHARED_CREATIVES,
		},
		createMockup:{
			...CREATE_MOCKUP,
			onClick:()=> createMockup(libraryId),
		},
		shareCreative:{
			...SHARE_CREATIVE,
			idArray: shareCreativeIds,
			onClick: () => dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, { creativeIds: shareCreativeIds })),
			name: `Share Creative${pluralize(shareCreativeIds)} (${shareCreativeIds.length}/${MAX_SHARED_CREATIVES})`,
			className: "bulk-external-link-action",
			disabled: shareCreativeIds.length > MAX_SHARED_CREATIVES,
		},
		previewVersions:{
			...PREVIEW_VERSIONS,
			idArray: previewVersionsIds,
			onClick:()=> previewBulkCreativeVersions(previewVersionsIds),
			name: `Preview Versions (${previewVersionsIds.length}/${MAX_SHARED_CREATIVES})`,
			className: "bulk-external-link-action",
			disabled: previewVersionsIds.length > MAX_SHARED_CREATIVES,
		},
		shareVersions:{
			...SHARE_VERSIONS,
			idArray: shareVersionsIds,
			onClick: () => dispatch(openModal(MODAL_TYPES.SHARE_CREATIVE_PREVIEWS, {
				creativeIds: shareVersionsIds,
				useVersionLinks: true,
			})),
			name: `Share Versions (${shareVersionsIds.length}/${MAX_SHARED_CREATIVES})`,
			className: "bulk-external-link-action",
			disabled: shareVersionsIds.length > MAX_SHARED_CREATIVES,
		},
		previewVersionPrivate:{
			icon: "fas fa-bolt",
			idArray: legacyPreviewVersionIds,
			onClick:()=> previewBulkCreativeVersionsLegacy(legacyPreviewVersionIds),
			name: `Preview Versions (Private) (${legacyPreviewVersionIds.length}/${MAX_SHARED_CREATIVES})`,
			disabled: legacyPreviewVersionIds.length > MAX_SHARED_CREATIVES,
		},
	};

	if(shouldDisplayAction.createMock(user)){
		splitActions.push(previewActions.createMockup);
	}

	for(const action in previewActions) {
		const { idArray, name } = previewActions[action];

		if(idArray?.length) {
			if(name.includes('Versions') && !divider) {
				splitActions.push({ divider: true });
				// only add one divider
				divider = true;
			}
			splitActions.push(previewActions[action]);
		}
	}

	return (
		<Button
			style="block"
			icon="preview"
			theme="blue"
			isDropDown
			addClass="true"
			className="bulk-preview-button"
			splitActions={splitActions}
			disabled = {!splitActions.length || isItemsInvalidDVAST}
			splitOptions={{ footerHeight: 117000 }}
			splitValue="bulk-preview"
		>
			Previews
		</Button>
	);
}

BulkPreviewButton.displayName = "BulkPreviewButton";
